import React from "react";
import styled from "styled-components";

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const PagerNav = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${(props) => (props.disabled ? "#b2b8c8" : "#4f566b")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  font-family: "DM Sans", sans-serif;
  font-size: 0.95rem;
  gap: 8px;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${(props) => (props.disabled ? "#b2b8c8" : "#1f2538")};
  }
`;

const PagerIcon = styled.span`
  font-size: 1.1rem;
  line-height: 1;
`;

const PagerPages = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const PagerPage = styled.button`
  align-items: center;
  background: ${(props) => (props.$active ? "#f3f5fb" : "transparent")};
  border: 1px solid ${(props) => (props.$active ? "#d9dde7" : "transparent")};
  border-radius: 10px;
  color: ${(props) => (props.$active ? "#1f2538" : "#4f566b")};
  cursor: pointer;
  display: inline-flex;
  font-family: "DM Sans", sans-serif;
  font-size: 0.95rem;
  height: 36px;
  justify-content: center;
  min-width: 36px;
  padding: 0 10px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${(props) => (props.$active ? "#f3f5fb" : "#f8f9fc")};
    border-color: ${(props) => (props.$active ? "#d9dde7" : "#eef1f7")};
    color: #1f2538;
  }
`;

const PagerEllipsis = styled.span`
  color: #8b91a1;
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  padding: 0 2px;
`;

const LogPagination = ({ total, limit, page, onPageChange }) => {
  const totalPages = Math.ceil(total / limit) || 1;
  const currentPage = page + 1;
  const visiblePages = [];

  if (totalPages <= 5) {
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
      visiblePages.push(pageNumber);
    }
  } else {
    visiblePages.push(1);

    if (currentPage > 3) {
      visiblePages.push("left-ellipsis");
    }

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber += 1) {
      if (pageNumber !== 1 && pageNumber !== totalPages) {
        visiblePages.push(pageNumber);
      }
    }

    if (currentPage < totalPages - 2) {
      visiblePages.push("right-ellipsis");
    }

    visiblePages.push(totalPages);
  }

  return (
    <Pagination>
      <PagerNav
        onClick={() => onPageChange(Math.max(0, page - 1))}
        disabled={page === 0}
      >
        <PagerIcon>&lsaquo;</PagerIcon>
        <span>Previous</span>
      </PagerNav>
      <PagerPages>
        {visiblePages.map((entry) => {
          if (typeof entry !== "number") {
            return <PagerEllipsis key={entry}>...</PagerEllipsis>;
          }

          return (
            <PagerPage
              key={entry}
              $active={entry === currentPage}
              onClick={() => onPageChange(entry - 1)}
            >
              {entry}
            </PagerPage>
          );
        })}
      </PagerPages>
      <PagerNav
        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        disabled={page + 1 >= totalPages}
      >
        <span>Next</span>
        <PagerIcon>&rsaquo;</PagerIcon>
      </PagerNav>
    </Pagination>
  );
};

export default LogPagination;
